import Coordinates from '@app/model/hotel/Coordinates';
import React, { useMemo, useState } from 'react';
import Map, { Layer, NavigationControl, Popup, Source } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import SearchHotelWithinRadiusResult from '@app/model/flexi_sets/SearchHotelWithinRadiusResult';
import FlexiSetMapTooltip from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/fragment/filter/FlexiSetMapTooltip';
import Spinner from '@app/component/static/Spinner';
import type { ViewStateChangeEvent } from 'react-map-gl/mapbox';

const FlexiSetMapFragment: React.FunctionComponent<{
	initialZoomLevel: number,
	areaHotels: SearchHotelWithinRadiusResult[],
	centralCoordinates: Coordinates,
	selectedIds: Set<number>,
	onAddToSet: (hotel: SearchHotelWithinRadiusResult) => void,
	onRemoveFromSet: (hotel: SearchHotelWithinRadiusResult) => void,
	onRefreshResults: (viewport: { longitude: number; latitude: number; zoom: number }) => void,
	isFiltering: boolean,
}> = ({ initialZoomLevel, areaHotels, centralCoordinates, selectedIds, onAddToSet, onRemoveFromSet, onRefreshResults, isFiltering }) => {
	// Map configuration
		const MAPBOX_TOKEN = 'pk.eyJ1IjoiaGVsb2NhYmFsdXMiLCJhIjoiY21md29sNXhoMDFleTJqb2JkemhhYnQ1eiJ9.ql14nA2BtTyW0LsbuAyGRg';

	return <div className="rounded-lg bg-white p-2 shadow h-[calc(100vh_-_300px)]">
		<MapboxMapComponent
			areaHotels={areaHotels}
			centralCoordinates={centralCoordinates}
			mapboxToken={MAPBOX_TOKEN}
			maxZoomLevel={initialZoomLevel}
			selectedIds={selectedIds}
			onAddToSet={onAddToSet}
			onRemoveFromSet={onRemoveFromSet}
			onRefreshResults={onRefreshResults}
		/>
		{/* Loader overlay when filtering */}
		{isFiltering && (
			<div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50 rounded">
				<div className="flex flex-col items-center gap-3">
					<Spinner width={12} height={12} className="w-12 h-12 text-violet-600 fill-violet-200 animate-spin" />
					<p className="text-sm font-medium text-gray-700">Filtering listings...</p>
				</div>
			</div>
		)}
	</div>;
};

const MapboxMapComponent: React.FunctionComponent<{
	areaHotels: SearchHotelWithinRadiusResult[],
	centralCoordinates: Coordinates,
	mapboxToken: string,
	maxZoomLevel: number,
	selectedIds: Set<number>,
	onAddToSet: (hotel: SearchHotelWithinRadiusResult) => void,
	onRemoveFromSet: (hotel: SearchHotelWithinRadiusResult) => void,
 	onRefreshResults: (viewport: { longitude: number; latitude: number; zoom: number }) => void,
}> = ({
				areaHotels,
				centralCoordinates,
				mapboxToken,
				maxZoomLevel,
				selectedIds,
				onAddToSet,
				onRemoveFromSet,
				onRefreshResults,
			}) => {
	const [refreshVisible, setRefreshVisible] = useState(false);
	const [pendingViewport, setPendingViewport] = useState<{ longitude: number; latitude: number; zoom: number } | null>(null);

	const handleMoveEnd = (e: ViewStateChangeEvent) => {
		const viewport = {
			longitude: e.viewState.longitude,
			latitude: e.viewState.latitude,
			zoom: e.viewState.zoom,
		};
		setPendingViewport(viewport);
		setRefreshVisible(true);
	};
	const [hoverInfo, setHoverInfo] = useState<{
		longitude: number;
		latitude: number;
		hotel: SearchHotelWithinRadiusResult;
	} | null>(null);

	const [clickInfo, setClickInfo] = useState<{
		longitude: number;
		latitude: number;
		hotel: SearchHotelWithinRadiusResult;
	} | null>(null);

	// Memoize selected id list for mapbox filters
	const selectedIdArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

	// Convert hotels to GeoJSON format for Mapbox
	const hotelGeoJSON = useMemo(() => {
		const features = areaHotels
			.filter(hotel => hotel.location?.coordinates)
			.map(hotel => ({
				type: 'Feature' as const,
				properties: {
					id: hotel.id,
					name: hotel.name,
					city: hotel.location?.city || 'N/A',
					address: hotel.location?.address || 'N/A',
					// Store only serializable data, not the full hotel object
				},
				geometry: {
					type: 'Point' as const,
					coordinates: [
						typeof hotel.location!.coordinates!.lng === 'string'
							? parseFloat(hotel.location!.coordinates!.lng)
							: hotel.location!.coordinates!.lng,
						typeof hotel.location!.coordinates!.lat === 'string'
							? parseFloat(hotel.location!.coordinates!.lat)
							: hotel.location!.coordinates!.lat,
					],
				},
			}));

		return {
			type: 'FeatureCollection' as const,
			features,
		};
	}, [areaHotels]);

	// Parse central coordinates
	const centerLng = typeof centralCoordinates.lng === 'string'
		? parseFloat(centralCoordinates.lng)
		: centralCoordinates.lng;
	const centerLat = typeof centralCoordinates.lat === 'string'
		? parseFloat(centralCoordinates.lat)
		: centralCoordinates.lat;

	// Handle hotel hover (show small tooltip)
	const onHotelHover = (event: any) => {
		const feature = event.features?.[0];

		if (feature) {
			// Set pointer cursor for hotel markers
			event.target.getCanvas().style.cursor = 'pointer';

			// Find hotel by ID from the original array
			const hotelId = feature.properties.id;
			const hotel = areaHotels.find(h => h.id === hotelId);

			if (hotel) {
				const [longitude, latitude] = feature.geometry.coordinates;

				setHoverInfo({
					longitude,
					latitude,
					hotel,
				});
			}
		} else {
			// Reset cursor and clear hover when not over any feature
			event.target.getCanvas().style.cursor = '';
			setHoverInfo(null);
		}
	};

	// Handle map click (open/close detailed tooltip)
	const onMapClick = (event: any) => {
		const feature = event.features?.[0];
		if (feature) {
			const hotelId = feature.properties.id;
			const hotel = areaHotels.find(h => h.id === hotelId);
			if (hotel) {
				const [longitude, latitude] = feature.geometry.coordinates;
				setClickInfo({ longitude, latitude, hotel });
			}
		} else {
			// Clicked on background -> close detailed tooltip
			setClickInfo(null);
		}
	};

	return (
		<Map
			mapboxAccessToken={mapboxToken}
			initialViewState={{
				longitude: centerLng,
				latitude: centerLat,
				zoom: maxZoomLevel,
			}}
			minZoom={14}
			maxZoom={maxZoomLevel}
			dragRotate={false}
			style={{ width: '100%', height: '100%' }}
			mapStyle="mapbox://styles/mapbox/streets-v12"
			interactiveLayerIds={['hotels-selected', 'hotels-unselected']}
			onMouseMove={onHotelHover}
			onClick={onMapClick}
			onMoveEnd={handleMoveEnd}
			onLoad={() => console.log('Mapbox map loaded successfully')}
			onError={(error) => console.error('Mapbox error:', error)}
		>
			{refreshVisible && pendingViewport && (
				<div style={{ position: 'absolute', top: 10, left: 0, right: 0, zIndex: 2, display: 'flex', justifyContent: 'center' }}>
					<button
						type="button"
						className="btn btn-primary shadow"
						onClick={() => {
							onRefreshResults(pendingViewport);
							setRefreshVisible(false);
						}}
					>
						Refresh results
					</button>
				</div>
			)}
			<div style={{ position: 'absolute', right: 10, top: 10, zIndex: 1 }}>
				<NavigationControl showCompass={false} />
			</div>
			<Source
				id="hotels"
				type="geojson"
				data={hotelGeoJSON}
			>
				{/* Selected hotels (red) */}
				<Layer
					id="hotels-selected"
					type="circle"
					source="hotels"
					filter={[
						'in', ['get', 'id'], ['literal', selectedIdArray],
					] as any}
					paint={{
						'circle-color': '#00a63e', // red-500
						'circle-radius': 8,
						'circle-stroke-width': 2,
						'circle-stroke-color': '#ffffff',
					}}
				/>

				{/* Unselected hotels (black) */}
				<Layer
					id="hotels-unselected"
					type="circle"
					source="hotels"
					filter={[
						'!', ['in', ['get', 'id'], ['literal', selectedIdArray]],
					] as any}
					paint={{
						'circle-color': '#5d0ec0',
						'circle-radius': 6,
						'circle-stroke-width': 2,
						'circle-stroke-color': '#ffffff',
					}}
				/>
			</Source>

			{/* Small hover tooltip: hotel name only */}
			{hoverInfo && (
				<Popup
					longitude={hoverInfo.longitude}
					latitude={hoverInfo.latitude}
					anchor="bottom"
					closeButton={false}
					closeOnClick={false}
					offset={[0, -10]}
					className="rp-mapbox-hover-popup"
					maxWidth="200px"
				>
					<div className="text-xs font-medium text-gray-700">{hoverInfo.hotel.name}</div>
				</Popup>
			)}

			{/* Detailed tooltip on click */}
			{clickInfo && (
				<Popup
					longitude={clickInfo.longitude}
					latitude={clickInfo.latitude}
					anchor="bottom"
					closeButton={false}
					closeOnClick={false}
					offset={[0, -10]}
					className="rp-mapbox-popup"
					maxWidth="420px"
				>
					<FlexiSetMapTooltip hotel={clickInfo.hotel}
															isSelected={selectedIds.has(clickInfo.hotel.id)}
															onAddToSet={() => onAddToSet(clickInfo.hotel)}
															onRemoveFromSet={() => onRemoveFromSet(clickInfo.hotel)}
															onClose={() => setClickInfo(null)} />
				</Popup>
			)}
		</Map>
	);
};


export default FlexiSetMapFragment;
